import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  getPutawayHistoryRequest,
  getPutawayPendingRequest,
  putawayBulkRequest,
  putawayRequest,
  putawayUploadRequest,
  restockRequest,
} from './putawayApi';
import { ALL_CLIENTS, PUTAWAY_TAB, buildBulkPutawayRequest, buildPutawayRequest } from './putawayUtils';
import { selectCurrentWarehouseId } from '../warehouseContext/warehouseContextSlice';
import { extractApiError } from '../../utils/errors';

/**
 * Loads the rows of the tab in use.
 *
 * The two pending tabs answer everything there is to put away, with the
 * counters of both; the history is paginated on the server. Without a
 * warehouse there is nothing to ask: putting away happens in one physical
 * place, and the endpoint demands it.
 *
 * @returns {Promise<{tab: string, rows: Array<object>, counts?: object, count?: number}>}
 */
export const fetchPutaway = createAsyncThunk(
  'putaway/fetchPutaway',
  async (_, { getState, rejectWithValue }) => {
    const state = getState();
    const { tab, company, page, rowsPerPage } = state.putaway.filters;
    const warehouseId = selectCurrentWarehouseId(state);

    if (!warehouseId) return { tab, rows: [], counts: null, count: 0 };

    try {
      const params = { warehouse: warehouseId };
      if (company !== ALL_CLIENTS) params.company = company;

      if (tab === PUTAWAY_TAB.HISTORY) {
        const { data } = await getPutawayHistoryRequest({ ...params, page, page_size: rowsPerPage });
        return { tab, rows: data.results || [], count: data.count || 0 };
      }

      const { data } = await getPutawayPendingRequest({ ...params, source: tab });
      return { tab, rows: data.results || [], counts: data.counts || null };
    } catch (error) {
      console.error('Error fetching putaway:', error);
      return rejectWithValue(
        extractApiError(error, 'No se pudo obtener lo pendiente de guardar')
      );
    }
  }
);

/**
 * Puts a pending row away, whole or in part, and reloads what is left.
 *
 * @param {{row: object, slotId: number, quantity: number|string, notes?: string}} params
 * @returns {Promise<{capacityWarning: string|null}>} The warning is a location
 *   left over its capacity: the system says so and does not block.
 */
export const putawayRow = createAsyncThunk(
  'putaway/putawayRow',
  async ({ row, slotId, quantity, notes }, { dispatch, getState, rejectWithValue }) => {
    try {
      const { isRestock, payload } = buildPutawayRequest(row, {
        slotId,
        quantity,
        notes,
        warehouseId: selectCurrentWarehouseId(getState()),
      });

      const { data } = await (isRestock ? restockRequest(payload) : putawayRequest(payload));
      dispatch(fetchPutaway());
      return { capacityWarning: data?.capacity_warning || null };
    } catch (error) {
      console.error('Error putting away:', error);
      return rejectWithValue(extractApiError(error, 'No se pudo guardar'));
    }
  }
);

/**
 * Puts away from a sheet, for one client in the warehouse in use. Rejects
 * with `{message, errors}` rather than a string, so the dialog can list the
 * refused rows by their line in the Excel.
 *
 * @param {{warehouseId: number, companyId: number, file: File}} params
 * @returns {Promise<{saved: number, units: number, capacity_warnings: string[], message: string}>}
 */
export const uploadPutaway = createAsyncThunk(
  'putaway/uploadPutaway',
  async ({ warehouseId, companyId, file }, { dispatch, rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('excel_file', file);
      formData.append('warehouse_id', warehouseId);
      formData.append('company_id', companyId);

      const { data } = await putawayUploadRequest(formData);
      dispatch(fetchPutaway());
      return data;
    } catch (error) {
      console.error('Error uploading putaway sheet:', error);
      return rejectWithValue({
        message: extractApiError(error, 'No se pudo cargar la planilla'),
        errors: error.response?.data?.errors || [],
      });
    }
  }
);

/**
 * Puts several rows away in one trip: each to its own suggestion, or all to
 * `slotId`. Rejects with `{message, errors}` rather than a string, so the
 * screen can name the rows the server refused.
 *
 * @param {{rows: Array<object>, slotId?: number|null, notes?: string}} params
 * @returns {Promise<{saved: number, units: number, capacity_warnings: string[], message: string}>}
 */
export const putawayRows = createAsyncThunk(
  'putaway/putawayRows',
  async ({ rows, slotId = null, notes }, { dispatch, getState, rejectWithValue }) => {
    try {
      const payload = buildBulkPutawayRequest(rows, {
        slotId,
        notes,
        warehouseId: selectCurrentWarehouseId(getState()),
      });
      const { data } = await putawayBulkRequest(payload);
      dispatch(fetchPutaway());
      return data;
    } catch (error) {
      console.error('Error putting rows away:', error);
      return rejectWithValue({
        message: extractApiError(error, 'No se pudieron guardar las filas'),
        errors: error.response?.data?.errors || [],
      });
    }
  }
);
